'use client';

import { cva } from 'class-variance-authority';
import React, { useState } from 'react';

import { Close } from '@videoblocks/react-icons';

import { cn } from '../../../utils';
import { Text } from '../Text/Text';
import { FontSize } from '../Text/types';
import { BadgeProps } from './types';

const badgeVariants = cva('flex justify-center items-center', {
  variants: {
    size: {
      small: 'px-1.5 py-0.5',
      medium: 'px-1.5 py-0.5',
      large: 'px-2 py-1.5',
    },
    color: {
      // include colors for hover state when theres a close button, work with design
      neutral: 'bg-white text-gray-900',
      info: 'bg-blue-700 text-white',
      success: 'bg-green-700 text-white',
      warning: 'bg-pink-700 text-white',
    },
    shape: {
      rounded: 'rounded-full',
      square: 'rounded',
    },
  },
  defaultVariants: {
    size: 'small',
    color: 'info',
  },
});

const iconVariants = cva('fill-current', {
  variants: {
    size: {
      small: 'w-2.5 h-2.5',
      medium: 'w-3.5 h-3.5',
      large: 'w-4 h-4',
    },
  },
});

const textVariants = cva('', {
  variants: {
    textCase: {
      capitalize: 'capitalize',
      uppercase: 'uppercase',
      lowercase: 'lowercase',
    },
  },
});

const Badge = ({
  className,
  color = 'info',
  hasCloseButton = false,
  Icon = null,
  shape = 'square',
  size = 'medium',
  text = 'New',
  textCase = 'capitalize',
  fontWeight = 'regular',
  ...rest
}: BadgeProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const fontSizeMap = {
    small: 'xs',
    medium: 'sm',
    large: 'base',
  };

  const fontSize = (fontSizeMap[size] || 'base') as FontSize;

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(badgeVariants({ size, color, shape }), 'gap-1', className)}
      {...rest}
    >
      {Icon && <Icon className={cn(iconVariants({ size }))} />}
      <Text
        asChild
        fontWeight={fontWeight}
        fontSize={fontSize}
        className={cn(textVariants({ textCase }))}
      >
        <span>{text}</span>
      </Text>

      {hasCloseButton ? (
        <button
          style={
            size === 'small'
              ? { width: '6px', height: '6px' }
              : { width: '8px', height: '8px' }
          }
          onClick={() => setIsOpen(false)}
        >
          <Close className="block fill-current" />
        </button>
      ) : null}
    </div>
  );
};

export { Badge, badgeVariants };
